import { useState, useEffect } from 'react';
import { CallFeed } from './components/CallFeed.js';
import { IntentChart } from './components/IntentChart.js';
import { TierChart } from './components/TierChart.js';
import { AgentRanking } from './components/AgentRanking.js';
import { BuyerTrends } from './components/BuyerTrends.js';
import { FlaggedCalls } from './components/FlaggedCalls.js';
import { trpc } from '../../lib/trpc.js';
import { getStoredUser } from '../../lib/auth.js';

type Tab = 'feed' | 'intelligence' | 'flagged';

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: 'feed', label: 'Call Feed', icon: '📞' },
  { id: 'intelligence', label: 'Intelligence', icon: '🧠' },
  { id: 'flagged', label: 'Flagged', icon: '⚠️' },
];

interface AnalyticsSummary {
  totalCalls: number;
  totalRevenue: number;
  avgDuration: number;
  connectedCalls: number;
}

function KPICard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-slate-100">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function KPICardSkeleton() {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 animate-pulse">
      <div className="h-3 bg-slate-800 rounded w-16 mb-2" />
      <div className="h-7 bg-slate-800 rounded w-24 mb-1" />
      <div className="h-3 bg-slate-800 rounded w-20" />
    </div>
  );
}

function formatDuration(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

export function QualityControlPage() {
  const [tab, setTab] = useState<Tab>('feed');
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [flagCount, setFlagCount] = useState(0);
  const [loadingKpi, setLoadingKpi] = useState(true);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoadingKpi(false); return; }

    Promise.all([
      trpc.calls.analytics.query({ tenantId: user.tenantId }),
      trpc.calls.flagged.query({ tenantId: user.tenantId }),
    ])
      .then(([analytics, flagged]) => {
        setSummary(analytics.summary);
        setFlagCount(flagged.length);
      })
      .catch(console.error)
      .finally(() => setLoadingKpi(false));
  }, []);

  const tierDistPercent = summary
    ? summary.totalCalls > 0
      ? Math.round((summary.connectedCalls / summary.totalCalls) * 100)
      : 0
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-3">
          <span>📞</span> Quality Control
        </h1>
        <p className="text-slate-400 mt-1 text-sm">Call analysis, transcription, and AI classification</p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {loadingKpi ? (
          <>
            <KPICardSkeleton />
            <KPICardSkeleton />
            <KPICardSkeleton />
            <KPICardSkeleton />
          </>
        ) : (
          <>
            <KPICard label="Total Calls" value={(summary?.totalCalls ?? 0).toString()} sub="Last 48 hours" />
            <KPICard
              label="Total Revenue"
              value={`$${(summary?.totalRevenue ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
              sub="Billed calls"
            />
            <KPICard
              label="Connected Calls"
              value={(summary?.connectedCalls ?? 0).toString()}
              sub={`${tierDistPercent}% of total`}
            />
            <KPICard label="Flagged" value={flagCount.toString()} sub="Pending review" />
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900 border border-slate-800 rounded-xl p-1 w-fit">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${tab === t.id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
          >
            <span>{t.icon}</span>
            {t.label}
            {t.id === 'flagged' && flagCount > 0 && (
              <span className="bg-rose-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none">
                {flagCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'feed' && <CallFeed />}

      {tab === 'intelligence' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <IntentChart />
            <TierChart />
          </div>
          <AgentRanking />
          <BuyerTrends />
        </div>
      )}

      {tab === 'flagged' && <FlaggedCalls />}
    </div>
  );
}
